"use client";
import { useSpring, animated, useInView } from '@react-spring/web';
import { CheckCircle, ArrowRight } from 'lucide-react';

export default function IntroductionSection() {
  const [openingRef, openingInView] = useInView({ once: true, rootMargin: '-80px' });
  const [leftCardRef, leftCardInView] = useInView({ once: true, rootMargin: '-80px' });
  const [rightCardRef, rightCardInView] = useInView({ once: true, rootMargin: '-80px' });
  const [closingRef, closingInView] = useInView({ once: true, rootMargin: '-80px' });
  
  const openingAnimation = useSpring({
    opacity: openingInView ? 1 : 0,
    config: { duration: 400 }
  });

  const leftCardAnimation = useSpring({
    opacity: leftCardInView ? 1 : 0,
    x: leftCardInView ? 0 : -20,
    config: { duration: 400 }
  });

  const rightCardAnimation = useSpring({
    opacity: rightCardInView ? 1 : 0,
    x: rightCardInView ? 0 : 20,
    config: { duration: 400 }
  });

  const closingAnimation = useSpring({
    opacity: closingInView ? 1 : 0,
    y: closingInView ? 0 : 20,
    config: { duration: 600 }
  });

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Powerful opening statement */}
        <animated.div
          ref={openingRef}
          className="text-center mb-16"
          style={openingAnimation}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading uppercase tracking-tight text-black mb-6">
            <span className="text-[#97d700]">87%</span> de los coaches no logran
            <span className="block">generar impacto real en sus clientes</span>
          </h2>
          <p className="text-xl md:text-2xl text-black/70 max-w-3xl mx-auto">
            No por falta de títulos ni de cursos… sino por falta de modelo, herramienta y metodología, que es lo que incorpora un verdadero agente de cambio
          </p>
        </animated.div>

        {/* The shift moment */}
        <div className="bg-gradient-to-r from-[#97d700]/10 to-transparent border-l-4 border-[#97d700] p-8 md:p-12 rounded-r-2xl mb-16 max-w-4xl mx-auto">
          <p className="text-2xl md:text-3xl font-heading uppercase tracking-wider text-black mb-4">
            Este es tu momento de <span className='text-[#97d700]'>cambio</span>
          </p>
          <p className="text-lg md:text-xl text-black/80">
            En estos 3 días te llevas mucho más que un diploma oficial avalado por la Federación Internacional de Coaching (ICF)
          </p>
        </div>

        {/* What you get vs what you become */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <animated.div
            ref={leftCardRef}
            className="bg-gray-50 p-8 rounded-2xl"
            style={leftCardAnimation}
          >
            <h3 className="text-2xl font-heading uppercase tracking-wider text-[#97d700] mb-6">
              Lo que recibes
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#97d700] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-bold text-lg">Modelo científico</p>
                  <p className="text-gray-600">Avalado por más de 35 años de investigación</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#97d700] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-bold text-lg">Metodología exclusiva</p>
                  <p className="text-gray-600">El valor de los valores con más de 3000 alumnos en más de 15 países.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#97d700] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-bold text-lg">Herramientas practicas</p>
                  <p className="text-gray-600">Aplicables desde el día 1</p>
                </div>
              </li>
            </ul>
          </animated.div>

          <animated.div
            ref={rightCardRef}
            className="bg-black text-white p-8 rounded-2xl"
            style={rightCardAnimation}
          >
            <h3 className="text-2xl font-heading uppercase tracking-wider text-[#97d700] mb-6">
              En quien te conviertes
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 text-[#97d700] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-bold text-lg">Agente de cambio</p>
                  <p className="text-gray-300">Provocas transformaciones profundas, no solo conversaciones</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 text-[#97d700] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-bold text-lg">Un líder coherente</p>
                  <p className="text-gray-300">Vives lo que enseñas, inspiras desde el ser</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 text-[#97d700] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-bold text-lg">Un profesional único</p>
                  <p className="text-gray-300">Con un factor diferencial que nadie más tiene</p>
                </div>
              </li>
            </ul>
          </animated.div>
        </div>

        {/* Closing power statement */}
        <animated.div
          ref={closingRef}
          className="text-center"
          style={closingAnimation}
        >
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-heading uppercase tracking-tight text-black mb-6">
            <span className="text-[#97d700]">3</span> días · <span className="text-[#97d700]">1</span> decisión
            <span className="block">
              Toda
              <span className="text-[#97d700]"> una vida</span> de impacto
            </span>
          </h3>

        </animated.div>
      </div>
    </section>
  );
}
